'use client'
import Section from 'components/Section'
import { Typography } from 'components/Contentful'
import {
  Box,
  TextField,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@achieve/ascend'
import { useEffect, useReducer, useRef } from 'react'
import { Button } from 'components/Button'
import { useMemoizedContentGetter } from 'utils/contentful'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { ExternalLink } from 'react-feather'

function AchieveProContactForm({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }

  const { fireAndForget } = useTriggerEvents()

  const memoizedContent = useMemoizedContentGetter(content, [
    'config',
    'ctaText',
    'responseText',
    'ctaResponse',
  ])

  const { config, ctaText, responseText, ctaResponse } = memoizedContent

  const title = config?.jsonContent?.title || 'Ready to grow your business?'
  const subtitle =
    config?.jsonContent?.subtitle ||
    'Apply now or request more information. You can also reach us directly at 833-694-1847.'

  const formLabels = config?.jsonContent?.formLabels || {
    companyName: 'Company name',
    contactName: 'Contact name / Requester *',
    title: 'Title',
    email: 'Email *',
    phone: 'Phone *',
    corporateAddress: 'Corporate address *',
    corporateNmls: 'Corporate NMLS #',
    comments: 'Comments',
  }

  const reducerFunction = (state, action) => {
    switch (action.type) {
      case 'setField':
        return { ...state, [action.fieldName]: action.fieldValue }
      case 'setError':
        return { ...state, [action.fieldName]: action.hasError }
      case 'setSent':
        return {
          ...state,
          sent: true,
          sentTitle: responseText?.textContent || 'Thank you!',
          redirectUrl: action.redirectUrl ?? ctaResponse?.linkHref,
        }
      case 'setSentError':
        return { ...state, sent: true, sentTitle: 'Oops! Something went wrong!' }
      case 'setFirstCheck':
        return { ...state, firstCheck: false }
      case 'setProgress':
        return { ...state, inProgress: action.fieldValue }
      case 'resetState':
        return { ...state, ...action.newState }
      default:
        return state
    }
  }

  const [
    {
      companyName,
      contactName,
      title: titleField,
      email,
      phoneNumber,
      corporateAddress,
      corporateNmls,
      comments,
      subscribeToDailyRateSheet,
      contactType,
      firstCheck,
      inProgress,
      sent,
      sentTitle,
      companyNameError,
      contactNameError,
      titleError,
      emailError,
      phoneNumberError,
      corporateAddressError,
      corporateNmlsError,
      redirectUrl,
    },
    dispatch,
  ] = useReducer(reducerFunction, {
    companyName: '',
    contactName: '',
    title: '',
    email: '',
    phoneNumber: '',
    corporateAddress: '',
    corporateNmls: '',
    comments: '',
    subscribeToDailyRateSheet: false,
    contactType: 'apply',
    firstCheck: true,
    inProgress: false,
    sent: false,
    sentTitle: '',
    companyNameError: false,
    contactNameError: true,
    titleError: false,
    emailError: true,
    phoneNumberError: true,
    corporateAddressError: true,
    corporateNmlsError: false,
    redirectUrl: null,
  })

  const validationRegexObject = {
    companyName: (value) => {
      return dispatch({
        type: 'setError',
        fieldName: 'companyNameError',
        hasError: !value || value.trim().length === 0,
      })
    },
    contactName: (value) => {
      return dispatch({
        type: 'setError',
        fieldName: 'contactNameError',
        hasError: !/^[\s\S,.'-]+$/i.test(value),
      })
    },
    title: (value) => {
      return dispatch({
        type: 'setError',
        fieldName: 'titleError',
        hasError: !value || value.trim().length === 0,
      })
    },
    email: (value) => {
      const emailRegex =
        /^(?!\.)(?!.*\.\.)([a-zA-Z0-9+._%-]+[a-zA-Z0-9+_%-])@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
      return dispatch({
        type: 'setError',
        fieldName: 'emailError',
        hasError: !emailRegex.test(value),
      })
    },
    phoneNumber: (value) => {
      return dispatch({
        type: 'setError',
        fieldName: 'phoneNumberError',
        hasError:
          !/^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/g.test(
            value
          ),
      })
    },
    corporateAddress: (value) => {
      return dispatch({
        type: 'setError',
        fieldName: 'corporateAddressError',
        hasError: !value || value.trim().length === 0,
      })
    },
    corporateNmls: (value) => {
      return dispatch({
        type: 'setError',
        fieldName: 'corporateNmlsError',
        hasError: !value || value.trim().length === 0,
      })
    },
  }

  const handleInputChange = (event) => {
    const fieldName = event.target.name
    const fieldValue = event.target.value

    if (fieldName === 'phoneNumber') {
      const formatted = formatPhoneNumber(fieldValue)
      validationRegexObject[fieldName](formatted)
      dispatch({
        type: 'setField',
        fieldName,
        fieldValue: formatted,
      })
    } else if (validationRegexObject[fieldName]) {
      validationRegexObject[fieldName](fieldValue)
      dispatch({
        type: 'setField',
        fieldName,
        fieldValue,
      })
    } else {
      dispatch({
        type: 'setField',
        fieldName,
        fieldValue,
      })
    }

    if (fieldName === 'contactType') {
      fireAndForget({
        event_action: 'button_click',
        event_type: 'button_click',
        track_event: 'internal_campaign_click',
        nav_link_section: 'Achieve Pro Contact Form',
        click_id: `contact_type_${fieldValue}`,
        click_text: fieldValue === 'apply' ? 'Apply now' : 'Request Info',
        sectionVariationObject,
      })
    }
  }

  const handleCheckboxChange = (event) => {
    dispatch({
      type: 'setField',
      fieldName: 'subscribeToDailyRateSheet',
      fieldValue: event.target.checked,
    })
  }

  const submitForm = () => {
    if (firstCheck) {
      dispatch({ type: 'setFirstCheck' })
    }
    sendMail()
  }

  const sendMail = async () => {
    if (!contactNameError && !emailError && !phoneNumberError && !corporateAddressError) {
      dispatch({ type: 'setProgress', fieldValue: true })
      const body = {
        company_name: companyName,
        contact_name: contactName,
        title: titleField,
        email: email,
        phone: phoneNumber.replace(/\D/g, ''),
        corporate_address: corporateAddress,
        corporate_nmls: corporateNmls,
        comments: comments,
        subscribe_to_daily_rate_sheet: subscribeToDailyRateSheet,
        contact_type: contactType,
      }
      const response = await fetch('/api/achieve-pro/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (response.status === 200) {
        const result = await response.json()
        dispatch({ type: 'setSent', redirectUrl: result?.data?.redirect_url })
        dispatch({ type: 'setProgress', fieldValue: false })
        fireAndForget({
          event_action: 'form_submit',
          event_type: 'form_submit',
          track_event: 'form_complete',
          nav_link_section: 'Achieve Pro Contact Form',
          form_name: 'achieve_pro_contact',
          click_text: ctaText?.textContent || 'Submit',
          sectionVariationObject,
        })
      } else {
        dispatch({ type: 'setSentError' })
        dispatch({ type: 'setProgress', fieldValue: false })
      }
    }
  }

  const formatPhoneNumber = (value) => {
    const cleaned = ('' + value).replace(/\D/g, '')
    const match = cleaned.match(/^(\d{1,3})(\d{1,3})?(\d{1,4})?$/)
    if (match) {
      const parts = [match[1], match[2], match[3]].filter(Boolean)
      return parts.join('-')
    }
    return value
  }

  const exitPage = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl
    }
  }

  const responseTitleRef = useRef(null)

  useEffect(() => {
    if (sent) {
      responseTitleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [sent])

  const whiteInput = { style: { backgroundColor: '#ffffff' } }

  return (
    <Section
      id="achieve-pro-contact-form"
      disableGutters
      className="py-[48px] px-[20px] breakpointMd:py-[80px] breakpointMd:px-[32px]"
    >
      <div className="bg-[#F8F9FC] rounded-[32px] py-[48px] px-[20px] breakpointMd:bg-background-secondary breakpointMd:rounded-[24px] breakpointMd:py-[48px] breakpointMd:px-[32px] breakpointLg:py-[64px] breakpointLg:px-[64px]">
        {sent ? (
          <Box className="flex flex-col items-center justify-center min-h-[400px]">
            <Box className="rounded-sm p-8 md:p-12 max-w-[600px] w-full text-center">
              <div ref={responseTitleRef} tabIndex={-1}>
                <Typography
                  content={sentTitle || 'Thank you for submitting your information!'}
                  variant={'headingLg'}
                  className="text-[28px] md:text-[32px] leading-tight font-semibold text-content-primary mb-4"
                />
              </div>
              <Typography
                content={responseText?.textContent || 'Our team will be reaching out shortly.'}
                variant={'bodySm'}
                className="text-[16px] text-content-primary leading-relaxed"
              />
              {redirectUrl && (
                <div className="flex justify-center mt-8">
                  <Button
                    onClick={() => exitPage()}
                    variant="contained"
                    color="primary"
                    className="px-8 py-3"
                    track={{
                      click_id: 'achieve_pro_continue',
                      nav_link_section: 'Achieve Pro Contact Form',
                      list_name: 'Achieve Pro Contact Form Continue',
                      click_text: ctaResponse?.linkText ?? 'Continue',
                      track_event: 'internal_campaign_click',
                      event_action: 'button_click',
                      event_type: 'button_click',
                      sectionVariationObject,
                    }}
                  >
                    <Typography fontWeight="bold" content={ctaResponse?.linkText ?? 'Continue'} />
                  </Button>
                </div>
              )}
            </Box>
          </Box>
        ) : (
          <div className="max-w-[1200px] mx-auto">
            <div className="flex flex-col gap-[24px] breakpointMd:gap-[32px]">
              <div className="w-full flex flex-col items-start breakpointMd:items-center breakpointMd:text-center">
                <Typography
                  variant="headingLg"
                  content={title}
                  className="mb-4 text-content-primary text-[40px] leading-tight font-semibold"
                />
                <p className="mb-0 text-content-primary text-base leading-relaxed">
                  {subtitle.split(/(\d{3}-\d{3}-\d{4})/).map((part, i) =>
                    /^\d{3}-\d{3}-\d{4}$/.test(part) ? (
                      <a
                        key={i}
                        href={`tel:${part.replace(/-/g, '')}`}
                        className="underline breakpointMd:no-underline breakpointMd:pointer-events-none breakpointMd:cursor-text breakpointMd:text-inherit"
                      >
                        {part}
                      </a>
                    ) : (
                      part
                    )
                  )}
                </p>
                <RadioGroup
                  row
                  name="contactType"
                  value={contactType}
                  onChange={handleInputChange}
                  className="mt-[24px] breakpointMd:mt-[48px] gap-[32px]"
                >
                  <FormControlLabel value="apply" control={<Radio />} label="Apply now" />
                  <FormControlLabel value="request" control={<Radio />} label="Request Info" />
                </RadioGroup>
              </div>

              {contactType === 'apply' && (
                <div className="mt-[32px] flex flex-col items-center">
                  <Button
                    href={config?.jsonContent?.applyUrl || 'http://achieve.loanscape.io/'}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="primary"
                    className="w-full breakpointMd:w-[335px] px-8 py-3 text-base font-semibold normal-case rounded-md bg-background-accent hover:bg-background-state-accentHover"
                    track={{
                      click_id: 'achieve_pro_apply_now',
                      nav_link_section: 'Achieve Pro Contact Form',
                      list_name: 'Achieve Pro Apply Now',
                      click_text: ctaText?.textContent || 'Apply now',
                      track_event: 'internal_campaign_click',
                      event_action: 'button_click',
                      event_type: 'button_click',
                      sectionVariationObject,
                    }}
                  >
                    <Typography fontWeight="bold" content={ctaText?.textContent || 'Apply now'} />
                    <ExternalLink size={18} className="ml-2" />
                  </Button>
                  <p
                    className="mt-[16px] text-left breakpointMd:text-center w-full breakpointMd:w-[335px]"
                    style={{
                      color: 'var(--content-secondary, #5C708A)',
                      fontFamily: 'Ultramarine',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '140%',
                    }}
                  >
                    {config?.jsonContent?.applyDisclosure ||
                      'You will be directed to our partner page to submit an application.'}
                  </p>
                </div>
              )}

              {contactType === 'request' && (
                <div className="w-full">
                  <style>{`.acpro-form .MuiOutlinedInput-root { background-color: #ffffff !important; }`}</style>
                  <Box className="acpro-form rounded-sm px-10 pb-10">
                    <Grid container rowSpacing={2} columnSpacing={4}>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          content={formLabels.companyName}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder="ex. Great Company, Inc."
                          fullWidth
                          InputProps={whiteInput}
                          name="companyName"
                          value={companyName}
                          onChange={handleInputChange}
                          error={!firstCheck && companyNameError}
                          helperText={
                            !firstCheck && companyNameError ? 'Company name is required' : ''
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          content={formLabels.contactName}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder="ex. John Doe"
                          fullWidth
                          InputProps={whiteInput}
                          name="contactName"
                          value={contactName}
                          onChange={handleInputChange}
                          error={!firstCheck && contactNameError}
                          helperText={
                            !firstCheck && contactNameError ? 'Contact name is required' : ''
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          content={formLabels.title}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder="ex. Correspondent"
                          fullWidth
                          InputProps={whiteInput}
                          name="title"
                          value={titleField}
                          onChange={handleInputChange}
                          error={!firstCheck && titleError}
                          helperText={!firstCheck && titleError ? 'Title is required' : ''}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          content={formLabels.email}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder="ex. abc@example.com"
                          fullWidth
                          InputProps={whiteInput}
                          name="email"
                          value={email}
                          onChange={handleInputChange}
                          error={!firstCheck && emailError}
                          helperText={!firstCheck && emailError ? 'Valid email is required' : ''}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                          content={formLabels.phone}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder="ex. ###-###-####"
                          fullWidth
                          InputProps={whiteInput}
                          name="phoneNumber"
                          value={phoneNumber}
                          onChange={handleInputChange}
                          error={!firstCheck && phoneNumberError}
                          helperText={
                            !firstCheck && phoneNumberError ? 'Valid phone is required' : ''
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          content={formLabels.corporateAddress}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder="ex. 123 Main Street, Anytown"
                          fullWidth
                          InputProps={whiteInput}
                          name="corporateAddress"
                          value={corporateAddress}
                          onChange={handleInputChange}
                          error={!firstCheck && corporateAddressError}
                          helperText={
                            !firstCheck && corporateAddressError
                              ? 'Corporate address is required'
                              : ''
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          content={formLabels.corporateNmls}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder="ex. ########"
                          fullWidth
                          InputProps={whiteInput}
                          name="corporateNmls"
                          value={corporateNmls}
                          onChange={handleInputChange}
                          error={!firstCheck && corporateNmlsError}
                          helperText={
                            !firstCheck && corporateNmlsError ? 'Corporate NMLS is required' : ''
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <Typography
                          content={formLabels.comments}
                          variant={'bodySm'}
                          className="mb-2 block text-content-primary text-sm font-medium"
                        />
                        <TextField
                          className="mb-2"
                          placeholder=""
                          fullWidth
                          multiline
                          InputProps={whiteInput}
                          rows={4}
                          name="comments"
                          value={comments}
                          onChange={handleInputChange}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }} className="mt-6">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={subscribeToDailyRateSheet}
                              onChange={handleCheckboxChange}
                              sx={{
                                color: '#4338ca',
                                '&.Mui-checked': {
                                  color: '#4338ca',
                                },
                              }}
                            />
                          }
                          label={
                            <span className="text-content-primary text-sm">
                              Subscribe to our Daily Rate Sheet
                            </span>
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }} className="mt-4 flex justify-start">
                        <Button
                          onClick={submitForm}
                          variant="contained"
                          color="primary"
                          disabled={inProgress}
                          className="min-w-[200px] px-8 py-3 text-base font-semibold normal-case rounded-md bg-background-accent hover:bg-background-state-accentHover"
                          track={{
                            click_id: 'achieve_pro_submit',
                            nav_link_section: 'Achieve Pro Contact Form',
                            list_name: 'Achieve Pro Contact Form Submit',
                            click_text: ctaText?.textContent || 'Submit',
                            track_event: 'internal_campaign_click',
                            event_action: 'button_click',
                            event_type: 'button_click',
                            sectionVariationObject,
                          }}
                        >
                          {inProgress ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            <Typography
                              fontWeight="bold"
                              content={ctaText?.textContent || 'Submit'}
                            />
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Section>
  )
}

export default AchieveProContactForm
